import { z } from 'zod'

import { EnumBuilder, type ZodSimplify } from '@mntn-dev/utility-types'
import { ModelFilterSchema } from '../model-filter-schema.ts'
import { NonEmptyStringSchema } from './property.models.ts'
import {
  DeliverableIdSchema,
  FileIdSchema,
  ProjectIdSchema,
  ProjectServiceIdSchema,
} from './unique-id.models.ts'

/**
 * DeliverableCategory
 */
export const [
  DeliverableCategories,
  DeliverableCategorySchema,
  DeliverableCategoryEnum,
] = EnumBuilder('video', 'file', 'archive')
export type DeliverableCategory = ZodSimplify<typeof DeliverableCategorySchema>

/**
 * VideoAspectRatio
 */
export const [VideoAspectRatios, VideoAspectRatioSchema, VideoAspectRatioEnum] =
  EnumBuilder('1x1', '2x3', '4x5', '9x16', '16x9', 'other')
export type VideoAspectRatio = ZodSimplify<typeof VideoAspectRatioSchema>

/**
 * VideoCutType
 */
export const [VideoCutTypes, VideoCutTypeSchema, VideoCutTypeEnum] =
  EnumBuilder('hero-cut', 'concept', 'cut-down', 'variation')
export type VideoCutType = ZodSimplify<typeof VideoCutTypeSchema>

/**
 * VideoDuration
 * TODO: Consider using a more specific type for duration, like: z.union([z.literal(15), z.literal(30)])
 */
export const VideoDurationFixedSchema = z.number().int().positive()
export type VideoDurationFixed = ZodSimplify<typeof VideoDurationFixedSchema>

export const VideoDurationRangeSchema = z
  .object({
    min: VideoDurationFixedSchema,
    max: VideoDurationFixedSchema,
  })
  .refine(({ min, max }) => min <= max, 'min must be less than or equal to max')
export type VideoDurationRange = ZodSimplify<typeof VideoDurationRangeSchema>

export const VideoDurationOptionsSchema = z.union([
  VideoDurationFixedSchema,
  VideoDurationRangeSchema,
])
export type VideoDurationOptions = ZodSimplify<
  typeof VideoDurationOptionsSchema
>

export const ArrayOfVideoDurationOptionsSchema = z
  .array(VideoDurationOptionsSchema)
  .nonempty()
export type ArrayOfVideoDurationOptions = ZodSimplify<
  typeof ArrayOfVideoDurationOptionsSchema
>

export const VideoDurationSchema = z.union([
  VideoDurationOptionsSchema,
  ArrayOfVideoDurationOptionsSchema,
])
export type VideoDuration = ZodSimplify<typeof VideoDurationSchema>

export function isVideoDurationRange(
  duration: VideoDuration
): duration is VideoDurationRange {
  return VideoDurationRangeSchema.safeParse(duration).success
}

export function isVideoDurationFixed(
  duration: VideoDuration
): duration is VideoDurationFixed {
  return VideoDurationFixedSchema.safeParse(duration).success
}

export function isVideoDurationArray(
  duration: VideoDuration
): duration is ArrayOfVideoDurationOptions {
  return ArrayOfVideoDurationOptionsSchema.safeParse(duration).success
}

/**
 * ChannelId
 */
export const [ChannelIds, ChannelIdSchema, ChannelIdEnum] = EnumBuilder(
  'facebook',
  'tiktok',
  'instagram',
  'ctv',
  'social',
  'linkedin',
  'other'
)
export type ChannelId = ZodSimplify<typeof ChannelIdSchema>

/**
 * DeliverableReviewLevel
 */
export const [
  DeliverableReviewLevels,
  DeliverableReviewLevelSchema,
  DeliverableReviewLevelEnum,
] = EnumBuilder('none', 'primary', 'secondary')
export type DeliverableReviewLevel = ZodSimplify<
  typeof DeliverableReviewLevelSchema
>

export const DeliverableNameSchema = NonEmptyStringSchema()

const DeliverableDetailsBaseSchema = z.object({
  name: DeliverableNameSchema.optional(),
  reviewLevel: DeliverableReviewLevelSchema,
})

export const VideoDeliverableDetailsSchema =
  DeliverableDetailsBaseSchema.extend({
    category: DeliverableCategorySchema.extract(['video']),
    cut: VideoCutTypeSchema,
    duration: VideoDurationSchema,
    channel: ChannelIdSchema.optional(),
    aspectRatio: VideoAspectRatioSchema.optional(),
  })

export type VideoDeliverableDetails = ZodSimplify<
  typeof VideoDeliverableDetailsSchema
>

export function isVideoDeliverable(
  deliverable: DeliverableDetails
): deliverable is VideoDeliverableDetails {
  return VideoDeliverableDetailsSchema.safeParse(deliverable).success
}

export const FileDeliverableDetailsSchema = DeliverableDetailsBaseSchema.extend(
  {
    category: DeliverableCategorySchema.extract(['file']),
  }
)

export type FileDeliverableDetails = ZodSimplify<
  typeof FileDeliverableDetailsSchema
>

export const ArchiveDeliverableDetailsSchema =
  DeliverableDetailsBaseSchema.extend({
    category: DeliverableCategorySchema.extract(['archive']),
  })

export type ArchiveDeliverableDetails = ZodSimplify<
  typeof ArchiveDeliverableDetailsSchema
>

export const DeliverableDetailsSchema = z.discriminatedUnion('category', [
  VideoDeliverableDetailsSchema,
  FileDeliverableDetailsSchema,
  ArchiveDeliverableDetailsSchema,
])

export type DeliverableDetails = ZodSimplify<typeof DeliverableDetailsSchema>

/**
 * DeliverableDomainSelectModel
 */
export const DeliverableDomainSelectModelSchema = z.object({
  deliverableId: DeliverableIdSchema,
  projectId: ProjectIdSchema,
  projectServiceId: ProjectServiceIdSchema,
  details: DeliverableDetailsSchema,
  fileId: FileIdSchema.optional(),
})

export type DeliverableDomainSelectModel = ZodSimplify<
  typeof DeliverableDomainSelectModelSchema
>

/**
 * DeliverableDomainInsertModel
 */
export const DeliverableDomainInsertModelSchema =
  DeliverableDomainSelectModelSchema.omit({ fileId: true }).partial({
    deliverableId: true,
  })

export type DeliverableDomainInsertModel = ZodSimplify<
  typeof DeliverableDomainInsertModelSchema
>

/**
 * DeliverableDomainUpdateModel
 */
export const DeliverableDomainUpdateModelSchema =
  DeliverableDomainSelectModelSchema.pick({
    fileId: true,
  }).partial()

export type DeliverableDomainUpdateModel = ZodSimplify<
  typeof DeliverableDomainUpdateModelSchema
>

/**
 * DeliverableDomainSelectModelFilter
 */
export const DeliverableDomainSelectModelFiltersSchema = z
  .object({
    deliverableId: ModelFilterSchema(DeliverableIdSchema),
    projectId: ModelFilterSchema(ProjectIdSchema),
    projectServiceId: ModelFilterSchema(ProjectServiceIdSchema),
    fileId: ModelFilterSchema(FileIdSchema),
  })
  .partial()

export type DeliverableDomainSelectModelFilters = ZodSimplify<
  typeof DeliverableDomainSelectModelFiltersSchema
>
